<script setup lang="ts">
import { type Ref as VueRef, ref, watch } from 'vue'
import { AutoFormLabel } from '@/foundation/auto-form'
import type { FieldProps } from '@/foundation/auto-form/interface'
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from '@/foundation/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/foundation/select'

defineProps<FieldProps & { config?: { inputProps: { options: { tiers: string[]; industries: string[] } } } }>()

const capturedComponentField: VueRef<any> = ref(null) // To store componentField from slotProps

const immediateErrorMessage = ref('')
const modelValue = ref<{ tier: string | undefined; industry: string | undefined }>({ tier: undefined, industry: undefined })

watch(
  () => capturedComponentField.value?.modelValue,
  (newValFromForm) => {
    if (newValFromForm && typeof newValFromForm === 'object') {
      const newTier = newValFromForm.tier || undefined
      const newIndustry = newValFromForm.industry || undefined

      if (modelValue.value.tier !== newTier || modelValue.value.industry !== newIndustry) {
        modelValue.value = {
          tier: newTier,
          industry: newIndustry,
        }
      }
    }
  },
  { deep: true, immediate: true },
)

watch(
  modelValue,
  (newModelValue) => {
    if (capturedComponentField.value?.['onUpdate:modelValue']) {
      capturedComponentField.value['onUpdate:modelValue']({
        tier: newModelValue.tier,
        industry: newModelValue.industry,
      })
    }
  },
  { deep: true },
)
</script>

<template>
  <FormField v-slot="localSlotProps" :name="fieldName">
    {{
      (() => {
        capturedComponentField = localSlotProps.componentField
        return ''
      })()
    }}
    <FormItem v-bind="$attrs">
      <FormControl>
        <div class="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-4">
          <div>
            <AutoFormLabel v-if="!config?.hideLabel" :required="required"> Tier </AutoFormLabel>
            <Select v-model="modelValue.tier">
              <SelectTrigger class="mt-1">
                <SelectValue :placeholder="!modelValue.tier ? 'Select Tier' : ''" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="tier in config?.inputProps?.options.tiers" :key="tier" :value="tier">
                  {{ tier }}
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage v-if="localSlotProps.errors?.some((error) => error.toLowerCase().includes('tier'))" />
          </div>
          <div>
            <AutoFormLabel v-if="!config?.hideLabel" :required="required"> Industry </AutoFormLabel>
            <Select v-model="modelValue.industry">
              <SelectTrigger class="mt-1">
                <SelectValue :placeholder="!modelValue.industry ? 'Select Industry' : ''" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="industry in config?.inputProps?.options.industries" :key="industry" :value="industry">
                  {{ industry }}
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage v-if="localSlotProps.errors?.some((error) => error.toLowerCase().includes('industry'))" />
          </div>
        </div>
        <div class="flex gap-2"></div>
      </FormControl>
      <FormDescription v-if="!immediateErrorMessage">
        {{ config?.description }}
      </FormDescription>
    </FormItem>
  </FormField>
</template>
